import { useCallback, useMemo } from 'react';
import { getIn } from 'formik';

import { FAutocomplete } from '../../../components/baseV2';
import FormFieldReadOnlyValue from './FormFieldReadOnlyValue';
import { useFormReadOnly } from './formReadOnlyProvider';
import { DISABLE_BROWSER_AUTOFILL } from '../constants';

import type { SyntheticEvent } from 'react';
import type { FormikValues } from 'formik';
import type { FAutocompleteOption } from '../../../components/baseV2';
import type { FormAutocompleteFieldProps } from '../types';

export default function FormAutocompleteField<TValues extends FormikValues>({
  formik,
  name,
  label,
  placeholder = `Select ${label.toLowerCase()}`,
  options,
  required = false,
  disabled = false,
  readOnly = false,
  inputAriaLabel,
  onBlur,
  onChange,
}: FormAutocompleteFieldProps<TValues>) {
  const readOnlyFromContext = useFormReadOnly();
  const isReadOnly = readOnly || readOnlyFromContext;
  const fieldError = getIn(formik.errors, name);
  const isTouched = Boolean(getIn(formik.touched, name));
  const errorMessage = isTouched ? fieldError?.toString() : undefined;
  // Suppress the redundant "required field" message for empty required fields; the field still
  // highlights via `error` and its required indicator. Other validation messages still show.
  const helperText = required && !getIn(formik.values, name) ? undefined : errorMessage;
  const value = useMemo(
    () => options.find((option) => option.value === getIn(formik.values, name)) ?? null,
    [formik.values, name, options],
  );

  const getOptionLabel = useCallback((option: FAutocompleteOption) => option.label, []);

  const isOptionEqualToValue = useCallback(
    (option: FAutocompleteOption, selectedOption: FAutocompleteOption) =>
      option.value === selectedOption.value,
    [],
  );

  const handleChange = useCallback(
    (_event: SyntheticEvent, option: FAutocompleteOption | null) => {
      const nextValue = option?.value ?? '';

      formik.setFieldValue(name, nextValue);

      onChange?.(name, nextValue);
    },
    [formik, name, onChange],
  );

  const handleBlur = useCallback(() => {
    formik.setFieldTouched(name, true);
    onBlur?.(name);
  }, [formik, name, onBlur]);

  if (isReadOnly) {
    return <FormFieldReadOnlyValue label={label} required={required} value={value?.label ?? ''} />;
  }

  return (
    <FAutocomplete
      disabled={disabled}
      error={Boolean(errorMessage)}
      fullWidth
      disableClearable={required}
      getOptionLabel={getOptionLabel}
      helperText={helperText}
      inputAriaLabel={inputAriaLabel}
      inputAutoComplete={DISABLE_BROWSER_AUTOFILL}
      isOptionEqualToValue={isOptionEqualToValue}
      label={label}
      onBlur={handleBlur}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      required={required}
      value={value}
    />
  );
}
